<template>
  <div>
    <div v-if="pageContent.subMenu">
      <zg-sub-menu
        name="zg-sub-menu"
        :selected-menu-item="page.path"
        :menu-items="pageContent.subMenu.items"
      />
    </div>
    <component
      :is="pageContent.hero.componentRef"
      v-if="pageContent.hero?.componentRef"
      :id="pageContent.hero.contentId"
      v-bind="pageContent.hero"
      :page-type="pageType"
      :product="product"
      :breadcrumbs="breadcrumbs"
    >
      <template
        v-if="pageContent.hero.text"
        #sub-title
      >
        <rich-text :body="pageContent.hero.text" />
      </template>

      <template
        v-for="(tab, index) in heroFragmentTabs"
        #[`module${index}`]
      >
        <fragment-component
          v-if="tab && showFragment"
          :ref="tab.fragment.path"
          :key="tab.contentId"
          :ssi="['ssi'].includes(heroFragment.type)"
          :ssr="['ssr', 'server', 'client'].includes(heroFragment.type)"
          :url="tab.fragment.path"
          :site="site"
          :fetch-fragment="fetchFragment"
        />
      </template>

      <template
        v-if="heroFragment"
        #module
      >
        <fragment-component
          v-if="heroFragment && showFragment"
          :ref="heroFragment.path"
          :ssi="['ssi'].includes(heroFragment.type)"
          :ssr="['ssr', 'server', 'client'].includes(heroFragment.type)"
          :url="heroFragment.path"
          :site="site"
          :fetch-fragment="fetchFragment"
        />

        <!-- dummy div that fills the module space if no fragment is showing -->
        <div v-else />
      </template>
    </component>
    <zg-hero
      v-else-if="pageContent.hero"
      :id="pageContent.hero.contentId"
      ref="hero"
      :title="pageContent.hero.title"
      :tabs="pageContent.hero.tabs"
      :page-type="pageType"
      :product="product"
      :background="pageContent.hero.background"
      :full-width-product-module="pageContent.hero.fullWidthProductModule"
      :breadcrumbs="breadcrumbs"
      :usps="pageContent.hero.usps"
      :tooltip-text="pageContent.hero.tooltipText"
    >
      <template
        v-if="pageContent.hero.text"
        #sub-title
      >
        <rich-text :body="pageContent.hero.text" />
      </template>

      <template
        v-for="(tab, index) in heroFragmentTabs"
        #[`module${index}`]
      >
        <fragment-component
          v-if="tab && showFragment"
          :ref="tab.fragment.path"
          :key="tab.contentId"
          :ssi="['ssi'].includes(heroFragment.type)"
          :ssr="['ssr', 'server', 'client'].includes(heroFragment.type)"
          :url="tab.fragment.path"
          :site="site"
          :fetch-fragment="fetchFragment"
        />
      </template>

      <template
        v-if="heroFragment"
        #module
      >
        <fragment-component
          v-if="heroFragment && showFragment"
          :ref="heroFragment.path"
          :ssi="['ssi'].includes(heroFragment.type)"
          :ssr="['ssr', 'server', 'client'].includes(heroFragment.type)"
          :url="heroFragment.path"
          :site="site"
          :fetch-fragment="fetchFragment"
        />

        <!-- dummy div that fills the module space if no fragment is showing -->
        <div v-else />
      </template>
    </zg-hero>

    <sticky-table-of-contents
      v-show="showSticky"
      :items="stoc"
    />

    <zg-highlighted-partners
      v-if="pageContent.highlightedPartners"
      :partners="pageContent.highlightedPartners.partners"
      :link="pageContent.highlightedPartners.link"
    />

    <page-sections
      v-if="pageContent.sections"
      :sections="pageContent.sections"
    />
  </div>
</template>

<script>
import { PageType } from '@ocp-zmarta/content-lib'

import FragmentComponent from '../components/FragmentComponent.vue'
import PageSections from '../components/PageSections.vue'
import RichText from '../components/RichText.vue'
import StickyTableOfContents from '../components/StickyTableOfContents.vue'
import { PAGE_TYPES, PRODUCTS } from '../utils/constants/content'

export default {
  name: 'LandingPage',
  components: {
    FragmentComponent,
    RichText,
    PageSections,
    StickyTableOfContents,
    ZgHero: () => import('@zc/components/ZgHero/ZgHero'),
    ZgHighlightedPartners: () => import('@zc/components/ZgHighlightedPartners/ZgHighlightedPartners'),
    ZgSubMenu: () => import('@zc/components/ZgSubMenu/ZgSubMenu')
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    site: {
      type: String,
      required: true
    }
  },
  data: () => ({
    showSticky: false,
    showStickyObserver: null,
    activeHeroProduct: null,
    activeHeroTab: 0,
    showFragment: true,
    preRenderFragment: true,
    fetchFragment: false
  }),
  computed: {
    pageType () {
      const contentType = this.pageContent?.contentType
      const isHomePage = (this.isZmarta && this.page?.path === '/home') || (!this.isElsklingNo && this.isElskling && this.page?.path === '/') // todo: change path to just "/" once the new version is final

      switch (contentType) {
        case PageType.ProductLandingPage:
          return isHomePage
            ? PAGE_TYPES.homePage
            : PAGE_TYPES.productPage
        default:
          return PAGE_TYPES.contentPage
      }
    },
    isHomePage () {
      return this.pageType === PAGE_TYPES.homePage
    },
    isZmarta () {
      return this.page?.sites?.some(item => item.includes('zmarta'))
    },
    isElskling () {
      return this.page?.sites?.some(item => item.includes('elskling'))
    },
    isElsklingNo () {
      return this.site === 'elskling.no'
    },
    product () {
      return PRODUCTS[this.page?.vertical]
    },
    heroFragmentTabs () {
      return this.isHomePage && this.pageContent.hero?.tabs ? this.pageContent.hero?.tabs : null
    },
    heroFragment () {
      return this.pageContent.hero?.fragment
    },
    breadcrumbs () {
      return this.page.breadcrumbs || []
    },
    pageContent () {
      return this.page.content || {}
    },
    stoc () {
      return this.page.stoc || []
    }
  },
  created () {
    if (this.page.content?.hero?.componentRef) {
      const heroComponent = this.page.content.hero?.componentRef
      this.$options.components[heroComponent] = () => {
        try {
          return import(`@customComponents/${heroComponent}.vue`)
        } catch { }
      }
    }
  },
  mounted () {
    if ('IntersectionObserver' in window) {
      this.showStickyObserver = new IntersectionObserver(([entry]) => {
        this.showSticky = this.stoc.length && !entry.isIntersecting
      })
    }

    if (this.$refs.hero) {
      this.showStickyObserver?.observe(this.$refs.hero.$el)
    }
  },
  beforeDestroy () {
    this.showStickyObserver?.disconnect()
  }
}
</script>
